import axios from "axios";
import { decodeHtml } from "./Utils";
import {
    fsDeleteMatch,
    fsDeleteOrphanedTeam,
    fsFetchAllMyMatches,
    fsFetchAllSharedMatches,
    fsFetchMatchViews,
    fsFetchMatchViewsForMatches,
    fsFetchSessionById,
    fsFetchTeamByNameAndSessionId,
    fsRecordMatchView,
    fsRemoveSharedMatchForRecipient,
    fsStoreTeam,
    usingFirestore,
} from "./firestoreData";

var networkErrorMessage = "A network error occurred. Please check your network connection. Consider using VPN if you are in a region with restricted access to our servers.";

export async function fetchTranslation(language) {
  const qs = require("qs");
  let data = qs.stringify({
    language: language,
  });

  let config = {
    method: "get",
    maxBodyLength: Infinity,
    url:
      process.env.REACT_APP_VBLIVE_API_URL +
      `/Session/GetTranslationByLanguage/${language}`,
    headers: {},
  };

  var logo = null;
  await axios
    .request(config)
    .then((response) => {
      // console.log(JSON.stringify(response.data));
      logo = response.data;
    })
    .catch((error) => {
      console.log(error);
      alert(networkErrorMessage);
    });
  return logo;
}

export async function fetchAllTranslations() {
  const qs = require("qs");
  let data = qs.stringify({});

  let config = {
    method: "get",
    maxBodyLength: Infinity,
    url: process.env.REACT_APP_VBLIVE_API_URL + "/Session/GetAllTranslations",
    headers: {},
    data: data,
  };

  var translations = null;
  await axios
    .request(config)
    .then((response) => {
      //   console.log(JSON.stringify(response.data));
      translations = response.data;
    })
    .catch((error) => {
      console.log(error);
      alert(networkErrorMessage);
    });
  return translations;
}

export async function fetchUserSettings(userEmail) {
  let config = {
    method: "get",
    maxBodyLength: Infinity,
    url:
      process.env.REACT_APP_VBLIVE_API_URL +
      `/Session/GetSettingsByUserEmail/${userEmail}`,
    headers: {},
  };

  var settings = null;
  await axios
    .request(config)
    .then((response) => {
      // console.log(JSON.stringify(response.data));
      settings = response.data;
    })
    .catch((error) => {
      console.log(error);
      alert(networkErrorMessage);
    });
  return settings;
}

export async function storeUserSettings(userEmail, settings) {
  const qs = require("qs");
  let data = qs.stringify({
    userEmail: userEmail,
    settings: JSON.stringify(settings),
  });

  let config = {
    method: "post",
    maxBodyLength: Infinity,
    url: process.env.REACT_APP_VBLIVE_API_URL + "/Session/StoreUserSettings",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    data: data,
  };

  await axios
    .request(config)
    .then((response) => {
      console.log(JSON.stringify(response.data));
    })
    .catch((error) => {
      console.log(error);
      alert(networkErrorMessage);
    });
}

export async function fetchAllMyMatches(userEmail) {
  if (usingFirestore()) {
    return await fsFetchAllMyMatches();
  }
  let config = {
    method: "get",
    maxBodyLength: Infinity,
    url: process.env.REACT_APP_VBLIVE_API_URL +
      `/Session/GetSessionInfoInServerForApp/${userEmail}/VBLive`,

    // url: process.env.REACT_APP_VBLIVE_API_URL.includes("vercel")
    //   ? process.env.REACT_APP_VBLIVE_API_URL +
    //     `/Session/GetSessionInfoInServerForApp/${userEmail}/VBLive`
    //   : process.env.REACT_APP_VBLIVE_API_URL +
    //     `/Session/GetSessionInfoInServerForApp?serverName=${userEmail}?appName=VBLive`,
    headers: {},
  };

  var matches = [];
  await axios
    .request(config)
    .then((response) => {
      matches = response.data;
      for (var match of matches) {
        match.teamA = decodeHtml(match.teamA);
        match.teamB = decodeHtml(match.teamB);
        match.description = decodeHtml(match.description);
        match.tournament = match.tournament ? decodeHtml(match.tournament) : "";
      }
    })
    .catch((error) => {
      console.log(error);
      alert(networkErrorMessage);
    });
  return matches;
}

export async function fetchAllSharedMatches(userEmail) {
  // Always query Firestore sharedGrants — they are written by the sharer and
  // must be visible to the recipient regardless of the recipient's own
  // migration status (new accounts are never migrated but can still receive
  // shared matches from migrated users).
  const [fsResult, legacyResult] = await Promise.allSettled([
    fsFetchAllSharedMatches(),
    (async () => {
      const config = {
        method: "get",
        maxBodyLength: Infinity,
        url: process.env.REACT_APP_VBLIVE_API_URL +
          `/Session/GetSharedSessionsForServer/${userEmail}/VBLive`,
        headers: {},
      };
      const response = await axios.request(config);
      const matches = response.data ?? [];
      for (const match of matches) {
        match.teamA = decodeHtml(match.teamA);
        match.teamB = decodeHtml(match.teamB);
        match.description = decodeHtml(match.description);
        match.tournament = match.tournament ? decodeHtml(match.tournament) : "";
      }
      return matches;
    })(),
  ]);

  const seen = new Set();
  const merged = [];
  for (const result of [fsResult, legacyResult]) {
    if (result.status === "fulfilled") {
      for (const m of result.value ?? []) {
        const key = String(m.id ?? "");
        if (!seen.has(key)) {
          seen.add(key);
          merged.push(m);
        }
      }
    } else {
      console.warn("fetchAllSharedMatches partial failure:", result.reason);
    }
  }
  merged.sort(
    (a, b) =>
      (b.sessionDateTimeInSeconds ?? 0) - (a.sessionDateTimeInSeconds ?? 0)
  );
  return merged;
}

export async function fetchSessionById(sessionId) {
  // Always try Firestore first — a shared match may have been written to
  // Firestore by a migrated sharer even when the recipient is a new (non-
  // migrated) account.
  const fsResult = await fsFetchSessionById(sessionId).catch(() => null);
  if (fsResult?.stats) return fsResult;

  // Fall back to the legacy REST API for non-migrated sharers.
  if (usingFirestore() && fsResult) {
    // Firestore found metadata but no stats buffer — don't bother with legacy.
    return fsResult;
  }
  let config = {
    method: "get",
    maxBodyLength: Infinity,
    url: process.env.REACT_APP_VBLIVE_API_URL +
      `/Session/GetSessionById/${sessionId}`,
    // url: process.env.REACT_APP_VBLIVE_API_URL.includes("vercel")
    //   ? process.env.REACT_APP_VBLIVE_API_URL +
    //     `/Session/GetSessionById/${sessionId}`
    //   : process.env.REACT_APP_VBLIVE_API_URL +
    //     `/Session/GetSessionsById?sessionId=${sessionId}`,
    headers: {},
  };

  var session = null;
  await axios
    .request(config)
    .then((response) => {
      if (!response.data || response.data.length === 0) {
        return;
      }
      session = response.data[0];
      if (!session) {
        return;
      }
      session.teamA = decodeHtml(session.teamA);
      session.teamB = decodeHtml(session.teamB);
      session.description = decodeHtml(session.description);
      session.tournament = session.tournament
        ? decodeHtml(session.tournament)
        : "";
    })
    .catch((error) => {
      console.log(error);
      alert(networkErrorMessage);
    });
  return session;
}

export async function deleteMatch(match) {
  if (usingFirestore()) {
    return await fsDeleteMatch(match);
  }
  let config = {
    method: "get",
    maxBodyLength: Infinity,
    url: process.env.REACT_APP_VBLIVE_API_URL.includes("vercel")
      ? process.env.REACT_APP_VBLIVE_API_URL +
        `/Session/DeleteSession/${match.id}`
      : process.env.REACT_APP_VBLIVE_API_URL +
        `/Session/DeleteSession?sessionId=${match.id}`,
    headers: {},
  };

  var ret = false;
  await axios
    .request(config)
    .then((response) => {
      // console.log(JSON.stringify(response.data));
      ret = true;
    })
    .catch((error) => {
      console.log(error);
      alert(networkErrorMessage);
    });
  return ret;
}

// Delete an orphaned team (by name) and any players belonging only to it.
// For Firestore users only — legacy users don't have managed team/player records.
export async function deleteOrphanedTeamAndPlayers(teamName) {
  if (usingFirestore()) {
    await fsDeleteOrphanedTeam(teamName);
  }
}

// Removes a shared match from the CURRENT USER's own "Shared Matches" list
// only -- unlike deleteMatch, this never touches the owner's match or any
// other recipient's access to it. No legacy (non-Firestore) equivalent
// exists yet, so this is a no-op there (same precedent as
// deleteOrphanedTeamAndPlayers above).
export async function removeSharedMatch(match) {
  if (usingFirestore()) {
    return await fsRemoveSharedMatchForRecipient(match.id);
  }
  return false;
}

export async function storeTeam(team) {
  if (usingFirestore()) {
    return await fsStoreTeam(team);
  }
  const qs = require("qs");
  let data = qs.stringify({
    name: team.name,
    sessionId: team.sessionId,
    players: team.players ? JSON.stringify(team.players) : "",
    lineup: team.lineup ? JSON.stringify(team.lineup) : "",
    isOppPassing: team.isOppPassing === true ? 1 : 0,
    isMBFollowingSetter: team.isMBFollowingSetter === true ? 1 : 0,
    logoURL: team.logoURL?? "",
    colour: team.colour ?? "",
  });

  let config = {
    method: "post",
    maxBodyLength: Infinity,
    url: process.env.REACT_APP_VBLIVE_API_URL + "/Session/StoreTeam",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    data: data,
  };

  await axios
    .request(config)
    .then((response) => {
      console.log(JSON.stringify(response.data));
    })
    .catch((error) => {
      console.log(error);
      alert(networkErrorMessage); 
    });
}

export async function fetchTeamById(teamId) {
  let config = {
    method: "get",
    maxBodyLength: Infinity,
    url: process.env.REACT_APP_VBLIVE_API_URL +
        `/Session/GetTeamById?id=${teamId}`,
    headers: {},
  };

  var team = null;
  await axios
    .request(config)
    .then((response) => {
      team = response.data[0];
    })
    .catch((error) => {
      console.log(error);
      alert(networkErrorMessage);
    });
  return team;
}

export async function fetchTeamByNameAndSessionId(name, sessionId) {  
  if (usingFirestore()) {
    return await fsFetchTeamByNameAndSessionId(name, sessionId);
  }
  let config = {
    method: 'get',
    maxBodyLength: Infinity,
    url: process.env.REACT_APP_VBLIVE_API_URL + `/Session/GetTeamByNameAndSessionId/${name}/${sessionId}`,
    headers: { },
  };
  
  var team = null;
  await axios.request(config)
  .then((response) => {
    console.log(JSON.stringify(response.data));
    team = response.data[0];
    if (team) {
      team.isMBFollowingSetter = team.isMBFollowingSetter ? team.isMBFollowingSetter === 1 : false;
      team.isOppPassing = team.isOppPassing ? team.isOppPassing === 1 : false;
    }
  })
  .catch((error) => {
    console.log(error);
    alert(networkErrorMessage);
  });
  return team;
}

export async function recordMatchView(matchId, viewerEmail) {
  return fsRecordMatchView(matchId, viewerEmail);
}

export async function fetchMatchViews(matchId) {
  return fsFetchMatchViews(matchId);
}

export async function fetchMatchViewsForMatches(matchIds) {
  return fsFetchMatchViewsForMatches(matchIds);
}
